/**
 * ReconcileModal: the modern "Mark as reconciled" dialog.
 * Opens with the date pre-filled to today, offers a one-tap "Today" chip, and
 * shows who will be recorded as the reconciler. Matches the VaultConfirmModal
 * design language (success variant).
 */
import { useEffect } from "react";
import { CheckCircle2, X, Check, Calendar } from "lucide-react";

export interface ReconcileModalProps {
  isOpen: boolean;
  /** Selected date as YYYY-MM-DD. */
  value: string;
  onChange: (date: string) => void;
  /** Max selectable date (defaults to today). */
  maxDate?: string;
  /** Display name of who will be recorded as the reconciler. */
  reconcilerName: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ReconcileModal({
  isOpen,
  value,
  onChange,
  maxDate,
  reconcilerName,
  onConfirm,
  onCancel,
}: ReconcileModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
      if (e.key === "Enter" && value) onConfirm();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, value, onConfirm, onCancel]);

  if (!isOpen) return null;

  return (
    <div className="vault-modal-overlay" onClick={onCancel}>
      <div
        className="vault-modal vault-confirm-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="vault-cfm-strip success" />

        <button className="vault-cfm-close" onClick={onCancel} aria-label="Close">
          <X size={16} />
        </button>

        <div className="vault-cfm-body">
          <div className="vault-cfm-icon success">
            <CheckCircle2 size={24} />
          </div>
          <h3 className="vault-cfm-title">Mark claim as reconciled</h3>
          <p className="vault-cfm-message">
            Confirm the date this claim was reconciled.
          </p>

          <div className="reconcile-field">
            <label className="reconcile-field-label" htmlFor="reconcile-date">
              Reconciliation date
            </label>
            <div className="reconcile-date-row">
              <div className="reconcile-date-input-wrap">
                <Calendar size={15} className="reconcile-date-icon" />
                <input
                  id="reconcile-date"
                  type="date"
                  className="reconcile-date-input"
                  value={value}
                  max={maxDate}
                  onChange={(e) => onChange(e.target.value)}
                  autoFocus
                />
              </div>
              <button
                type="button"
                className="reconcile-today-chip"
                onClick={() => maxDate && onChange(maxDate)}
              >
                Today
              </button>
            </div>
            <p className="reconcile-by">
              Reconciled by <strong>{reconcilerName}</strong>
            </p>
          </div>
        </div>

        <div className="vault-cfm-actions">
          <button className="vault-cfm-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="vault-cfm-confirm success"
            onClick={onConfirm}
            disabled={!value}
          >
            <Check size={14} />
            Mark reconciled
          </button>
        </div>
      </div>
    </div>
  );
}
